import React from "react";
import { getListQueryStatus, IndexForms } from "@hex-insights/app-modules";
import {
	Button,
	Column,
	compareObjects,
	compareStringsAsc,
	Conditional,
	Else,
	Heading,
	If,
	isSameTimeOrBefore,
	Modal,
	Row,
	sort,
	Tooltip,
	TruncatedText,
} from "@hex-insights/core";
import { EditMode } from "@hex-insights/forms";
import { EmptyView } from "../../../../Components/Empty";
import { Icon } from "../../../../Components/Icon";
import { SearchField } from "../../../../Components/Search";
import {
	CourseAttendanceRecordListQuery,
	CourseAttendanceRecordOrderField,
	CourseSectionPeriod,
	mergeFilterInputs,
	OrderDirection,
	Student,
	StudentNameAndImageFragment,
	useCourseAttendanceRecordListQuery,
	useStudentNamesAndImagesQuery,
} from "../../../../Utilities";
import { StudentFilterFormState, StudentFilterFormUtils } from "../../../Filter";
import { StudentFormat } from "../../../Format";
import { CourseAttendanceRecordPeriodForm } from "../../../Forms";
import { StudentLink } from "../../../Links";
import { StudentUtilities } from "../../../Utilities";
import { AttendanceStatusBadge } from "../../AttendanceStatus";
import { StudentIcon } from "../../Student";
import { StudentNoteCreateModalController } from "../../StudentNote";
import styles from "./styles.module.css";

type CourseSectionPeriodStudentsListProps = {
	courseSectionPeriodID: CourseSectionPeriod["id"];
	date: string;
};

export function CourseSectionPeriodStudentsList({ courseSectionPeriodID, date }: CourseSectionPeriodStudentsListProps) {
	const filterFormState = StudentFilterFormState.useFilterFormState();
	const filterInputs = IndexForms.useFilterInput(filterFormState.formValues, StudentFilterFormUtils.toFilterInputs);

	const {
		loading: loadingStudents,
		data: studentsData,
		error: studentsError,
	} = useStudentNamesAndImagesQuery({
		variables: {
			filters: mergeFilterInputs(
				filterInputs,
				StudentUtilities.getEnrolledInCourseSectionWithOnDateFilterInput(
					{ courseSectionPeriods: [{ idEQ: courseSectionPeriodID }] },
					date,
				),
			),
		},
	});
	const sortedStudentEdges = React.useMemo(() => {
		if (!studentsData) {
			return [];
		}
		return sort(
			studentsData.studentConnection.edges,
			compareObjects((e) => StudentFormat.name(e.node), compareStringsAsc()),
		);
	}, [studentsData]);
	const studentsQueryStatus = getListQueryStatus({
		loading: loadingStudents,
		data: studentsData,
		error: studentsError,
		resultKey: "studentConnection",
	});

	const { data: attendanceData } = useCourseAttendanceRecordListQuery({
		variables: {
			filters: { courseSectionPeriodIDEQ: courseSectionPeriodID, attendanceDateEQ: date },
			order: { field: CourseAttendanceRecordOrderField.CreatedAt, direction: OrderDirection.Desc },
		},
		fetchPolicy: "network-only",
		pollInterval: 5 * 60 * 1000,
	});
	const needsAttendance = !!attendanceData && attendanceData.courseAttendanceRecordConnection.edges.length === 0;
	const studentAttendanceMap = React.useMemo(() => {
		const map: Record<
			Student["id"],
			CourseAttendanceRecordListQuery["courseAttendanceRecordConnection"]["edges"][0]["node"]
		> = {};
		if (!attendanceData) {
			return map;
		}

		for (let i = 0; i < attendanceData.courseAttendanceRecordConnection.edges.length; i++) {
			const attendanceRecord = attendanceData.courseAttendanceRecordConnection.edges[i].node;
			if (map[attendanceRecord.student.id]) {
				continue;
			}
			map[attendanceRecord.student.id] = attendanceRecord;
		}

		return map;
	}, [attendanceData]);

	const isPeriodTodayOrEarlier = isSameTimeOrBefore(date, new Date(), "day");
	const canTakeIndividualAttendance = isPeriodTodayOrEarlier && !needsAttendance;

	return (
		<React.Fragment>
			<SearchField formState={filterFormState} name="search" autoFocus={false} style={{ marginBottom: "1rem" }} />

			<Conditional>
				<If condition={studentsQueryStatus === "results"}>
					<Column style={{ flexGrow: 1 }}>
						{sortedStudentEdges.map((e) => (
							<StudentView
								key={e.node.id}
								student={e.node}
								attendanceRecord={studentAttendanceMap[e.node.id]}
								canTakeAttendance={canTakeIndividualAttendance}
								courseSectionPeriodID={courseSectionPeriodID}
								date={date}
							/>
						))}
					</Column>
				</If>
				<If condition={studentsQueryStatus === "no-results"}>
					<EmptyView
						IconComponent={Icon.Student}
						primaryText="Students"
						secondaryText="No students are enrolled in this class"
					/>
				</If>
				<If condition={studentsQueryStatus === "loading"}>Loading...</If>
				<If condition={studentsQueryStatus === "error"}>Something went wrong</If>
			</Conditional>
		</React.Fragment>
	);
}

type StudentViewProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	attendanceRecord?: CourseAttendanceRecordListQuery["courseAttendanceRecordConnection"]["edges"][0]["node"];
	canTakeAttendance: boolean;
	courseSectionPeriodID: CourseSectionPeriod["id"];
	date: string;
};

function StudentView({ student, attendanceRecord, canTakeAttendance, courseSectionPeriodID, date }: StudentViewProps) {
	const studentNoteFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				primaryStudentIDs: [student.id],
			},
		}),
		[student.id],
	);

	return (
		<Row justify="space-between" align="center" className={styles["student"]}>
			<Row justify="spaced-start" align="center">
				<StudentIcon student={student} size={32} />
				<StudentLink instance={student} className="link--no-text-decoration">
					<TruncatedText>{StudentFormat.name(student)}</TruncatedText>
				</StudentLink>
			</Row>

			<Row justify="spaced-end" align="center">
				<Conditional>
					<If condition={canTakeAttendance}>
						<StudentCourseSectionAttendanceModalController
							student={student}
							attendanceRecord={attendanceRecord}
							courseSectionPeriodID={courseSectionPeriodID}
							date={date}
						/>
					</If>
					<Else>
						<AttendanceStatusBadge attendanceStatus={attendanceRecord?.attendanceStatus ?? null} />
					</Else>
				</Conditional>

				<StudentNoteCreateModalController
					variant="link"
					size="small"
					formStateArgs={studentNoteFormStateArgs}
					formNameSuffix={student.id}
					className="nowrap"
				>
					Add Note
				</StudentNoteCreateModalController>
			</Row>
		</Row>
	);
}

type StudentCourseSectionAttendanceModalControllerProps = {
	student: Pick<Student, "id"> & StudentNameAndImageFragment;
	attendanceRecord:
		| CourseAttendanceRecordListQuery["courseAttendanceRecordConnection"]["edges"][0]["node"]
		| null
		| undefined;
	courseSectionPeriodID: CourseSectionPeriod["id"];
	date: string;
};

function StudentCourseSectionAttendanceModalController({
	student,
	attendanceRecord,
	courseSectionPeriodID,
	date,
}: StudentCourseSectionAttendanceModalControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	const formNameSuffix = courseSectionPeriodID + "." + date + "." + student.id;

	const detailFormStateArgs = React.useMemo(
		() => ({
			editMode: EditMode.WriteOnly,
		}),
		[],
	);

	const createFormStateArgs = React.useMemo(
		() => ({
			initialFormValues: {
				courseSectionPeriodID,
				studentID: student.id,
				attendanceDate: date,
			},
		}),
		[courseSectionPeriodID, student.id, date],
	);

	return (
		<React.Fragment>
			<Tooltip.Container>
				<Tooltip className="tooltip--inverted">
					<Tooltip.Body>
						<span className="nowrap">Click to edit</span>
					</Tooltip.Body>
				</Tooltip>

				<Button onClick={openModal} style={{ padding: 0, border: "none" }}>
					<AttendanceStatusBadge attendanceStatus={attendanceRecord?.attendanceStatus ?? null} />
				</Button>
			</Tooltip.Container>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal} className={styles["student-attendance-modal"]}>
					<Modal.Header>
						<Row justify="spaced-start" align="center">
							<StudentIcon student={student} size={48} />
							<Heading.H2 noMargin>
								<TruncatedText>{StudentFormat.name(student)}</TruncatedText>
							</Heading.H2>
						</Row>
					</Modal.Header>
					<Modal.Body>
						<Conditional>
							<If condition={!!attendanceRecord}>
								{!!attendanceRecord && (
									<CourseAttendanceRecordPeriodForm.ControlledDetail
										courseAttendanceRecord={attendanceRecord}
										formStateArgs={detailFormStateArgs}
										formNameSuffix={formNameSuffix}
										onSuccess={closeModal}
										style={{ height: "100%", display: "flex", flexDirection: "column" }}
									/>
								)}
							</If>
							<Else>
								<CourseAttendanceRecordPeriodForm.ControlledCreate
									formStateArgs={createFormStateArgs}
									formNameSuffix={formNameSuffix}
									onSuccess={closeModal}
									style={{ height: "100%", display: "flex", flexDirection: "column" }}
								/>
							</Else>
						</Conditional>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}
